import React, { Suspense, lazy } from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, matchRoutes, useLocation, useNavigate } from "react-router-dom";
import { ErrorBoundary } from "./components/layout/ErrorBoundary";
import { ThemeProvider } from "./providers/ThemeProvider";
import { knownRoutePatterns } from "./router/known-paths";
import "@/styles/index.css";

/** 懒加载完整应用（含 AuthProvider / SWR / 路由），404 时不会下载 */
const LazyAppShell = lazy(() => import("./AppShell").then((m) => ({ default: m.AppShell })));

/** 轻量 404 页面，不加载 AuthProvider / SWR 等 */
const LightNotFound: React.FC = () => {
	const navigate = useNavigate();
	return (
		<div className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-6 bg-gray-50 dark:bg-gray-900">
			<div className="text-6xl font-bold text-gray-300 dark:text-gray-600">404</div>
			<div className="text-lg text-gray-500 dark:text-gray-400">
				抱歉，您访问的页面不存在或已被移除。
			</div>
			<div className="flex gap-3">
				<button
					type="button"
					className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700"
					onClick={() => navigate("/")}
				>
					返回首页
				</button>
				<button
					type="button"
					className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800"
					onClick={() => navigate(-1)}
				>
					返回上一页
				</button>
			</div>
		</div>
	);
};

/** 路由守卫：未匹配已知路由时直接渲染轻量 404，不初始化 Auth */
const RouteGate: React.FC = () => {
	const location = useLocation();
	const matched = matchRoutes(knownRoutePatterns, location);
	if (!matched) {
		return <LightNotFound />;
	}
	return (
		<Suspense
			fallback={
				<div className="flex h-screen items-center justify-center">
					<div className="size-8 animate-spin rounded-full border-4 border-zinc-700 border-t-emerald-400" />
				</div>
			}
		>
			<LazyAppShell />
		</Suspense>
	);
};

ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
	<React.StrictMode>
		<ThemeProvider>
			<BrowserRouter>
				<ErrorBoundary>
					<RouteGate />
				</ErrorBoundary>
			</BrowserRouter>
		</ThemeProvider>
	</React.StrictMode>,
);
